/**
 * DebugManagerHotReload.ts
 *
 * Hot-reload system for override JSON files during gameplay.
 * Allows reloading weapons-overrides.json, projectiles-overrides.json, etc.
 * without restarting the game.
 */

import Phaser from 'phaser';
import { DataManagerWeapon } from '../weapons/DataManagerWeapon';
import { DataManagerProjectile } from '../projectiles/DataManagerProjectile';
import { FloatingNotification } from '../ui/FloatingNotification';

interface OverrideFile {
  key: string;
  path: string;
}

const OVERRIDE_FILES: OverrideFile[] = [
  { key: 'weapons-overrides', path: '/assets/data/weapons-overrides.json' },
  { key: 'projectiles-overrides', path: '/assets/data/projectiles-overrides.json' },
  { key: 'entities-overrides', path: '/assets/data/entities-overrides.json' },
];

/**
 * Hot-reload manager for override JSON files.
 * Fetches fresh data from server and reloads data managers.
 */
export class DebugManagerHotReload {
  /**
   * Reload all override files and refresh data managers.
   * @param scene - Current Phaser scene (needed for cache access)
   * @returns Promise resolving to success/failure status
   */
  static async reloadOverrides(scene: Phaser.Scene): Promise<boolean> {
    console.log('%c[HotReload] Reloading override files...', 'color: #ffa500; font-weight: bold');

    try {
      // Fetch all override files in parallel with cache-busting timestamp
      const timestamp = Date.now();
      const fetchPromises = OVERRIDE_FILES.map(async (file) => {
        const url = `${file.path}?t=${timestamp}`;
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch ${file.path}: ${response.status}`);
        }
        const data = await response.json();
        return { key: file.key, data };
      });

      const results = await Promise.all(fetchPromises);

      // Update Phaser cache with fresh data
      for (const { key, data } of results) {
        // Remove old cached data
        if (scene.cache.json.exists(key)) {
          scene.cache.json.remove(key);
        }
        // Add fresh data
        scene.cache.json.add(key, data);
        console.log(`[HotReload] Updated cache: ${key}`);
      }

      // Reset and reload data managers
      DataManagerWeapon.reset();
      DataManagerProjectile.reset();

      DataManagerWeapon.load(scene);
      DataManagerProjectile.load(scene);

      // Reload DataManagerEntity overrides if available
      const prefabManager = scene.registry.get('prefabManager');
      if (prefabManager && typeof prefabManager.loadOverrides === 'function') {
        prefabManager.loadOverrides('entities-overrides');
        console.log('[HotReload] Reloaded DataManagerEntity overrides');
      }

      console.log('%c[HotReload] Data reloaded successfully!', 'color: #00ff00; font-weight: bold');
      return true;
    } catch (error) {
      console.error('[HotReload] Failed to reload:', error);
      return false;
    }
  }

  /**
   * Show visual feedback notification in-game.
   * @param scene - Current Phaser scene
   * @param success - Whether reload was successful
   */
  static showNotification(scene: Phaser.Scene, success: boolean): void {
    const message = success ? 'Data Reloaded!' : 'Reload Failed!';
    const position = { x: scene.scale.width / 2, y: 100 };

    if (success) {
      FloatingNotification.success(scene, message, { position, isHUD: true, fontSize: 24 });
    } else {
      FloatingNotification.error(scene, message, { position, isHUD: true, fontSize: 24 });
    }
  }
}
